import type { BoxProps } from '@chakra-ui/react';
import { Box, Flex, Text, useBreakpointValue } from '@chakra-ui/react';
import type { StatsMetricValue } from '@ui/hooks/stats/types';
import { formatNumber } from '@ui/utils/format';
import { flattenDeep, max } from 'lodash-es';
import { useMemo } from 'react';

import { DynamicHighchartsReact } from '../DynamicHighchartsReact';
import StatsChartTitle from './StatsChartTitle';
import { useChartDefaultColorList } from './utils';

type StatsColumnChartSeries = {
  data: {
    y: number;
    x: number;
  }[];
  color?: string;
  label: string;
}[];

export type StatsColumnChartProps = Omit<
  BoxProps,
  'w' | 'h' | 'width' | 'height'
> & {
  title: string;
  values: Record<string, StatsMetricValue[] | undefined>;
  width?: number | string | null;
  height?: number | string | null;
};

export const StatsColumnChart = ({
  title,
  values,
  width = null,
  height,
  ...props
}: StatsColumnChartProps) => {
  const defaultHeight = useBreakpointValue({ base: 155, desktop: 210 });
  const defaultColorList = useChartDefaultColorList();
  const chartOptions = useChartOptions({
    title,
    values,
    width,
    height: height ?? defaultHeight,
  });

  return (
    <>
      <Box {...props}>
        {title ? <StatsChartTitle text={title} color="text-light" /> : null}
        <DynamicHighchartsReact options={chartOptions} />
        {Object.keys(values).length ? (
          <Flex
            wrap="wrap"
            mt={{
              base: '13px',
              desktop: '17px',
            }}
          >
            {Object.keys(values).map((label, i) => {
              if (!label) {
                return null;
              }
              return (
                <Flex mr={6} key={i} align="center">
                  <Box
                    w="20px"
                    h="5px"
                    borderRadius="999px"
                    bgColor={defaultColorList[i]}
                  />
                  <Text color="text-light" textStyle="bodySmall" ml="4px">
                    {label}
                  </Text>
                </Flex>
              );
            })}
          </Flex>
        ) : null}
      </Box>
    </>
  );
};

const useChartOptions = ({
  values,
  width,
  height,
}: {
  title: string;
  values: Record<string, StatsMetricValue[] | undefined>;
  width?: number | string | null;
  height?: number | string | null;
}): Highcharts.Options => {
  const defaultColorList = useChartDefaultColorList();

  const series: StatsColumnChartSeries = useMemo(() => {
    const keys = Object.keys(values);

    return keys.map((key, i) => ({
      label: key,
      color: defaultColorList[i],
      data: (values[key] || []).map((val) => ({
        y: val.value,
        x: Number(val.timestamp) * 1000,
      })),
    }));
  }, [defaultColorList, values]);

  const options: Highcharts.Options = {
    chart: {
      width: width,
      height: height,
      animation: false,
      backgroundColor: 'transparent',
      spacing: [0, 2, 2, 2],
    },
    credits: {
      enabled: false,
    },
    title: {
      text: '',
    },
    legend: {
      enabled: false,
    },
    xAxis: {
      type: 'datetime',
      crosshair: true,
      tickmarkPlacement: 'on',
      labels: {
        enabled: false,
      },
      tickWidth: 0,
    },
    yAxis: {
      min: 0,
      visible: false,
      title: {
        text: '',
      },
      max: max(flattenDeep(series.map((v) => v.data.map((v) => v.y)))),
    },
    tooltip: {
      useHTML: true,
      shared: true,
      backgroundColor: '#EBF0F3',
      borderWidth: 0,
      borderRadius: 5,
      shadow: true,
      xDateFormat: '<div style="font-size: 12px" color="grey">%Y-%m-%d</div>',
      pointFormatter() {
        const dot = `<span style="color: ${this.color};">●</span>`;
        const valueHtml = `<span>${this.series.name} ${formatNumber(
          this.y ?? 0,
          2,
          true,
          true,
        )}</span>`;
        return `<div style="font-size: 12px">${dot} ${valueHtml}</div>`;
      },
    },
    plotOptions: {
      column: {
        pointPadding: 0,
        borderWidth: 0,
      },
    },
    series: series.map((item, i) => {
      return {
        type: 'column',
        name: item.label,
        data: item.data,
        color: item.color || defaultColorList[i],
      };
    }),
  };

  return options;
};
